import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from 'framer-motion';
import {
  Mail, BarChart3, Users, MapPin, PawPrint, CheckCircle2,
  ArrowRight, Shield, Zap, Monitor, Clock, ChevronRight,
  Star, Play, X, Phone, Building2, Sparkles,
  Bath, Home, TrendingUp, TrendingDown, Activity, Calendar,
  DollarSign, LineChart, PieChart, LayoutDashboard, Grid3x3,
  Syringe, FileSignature, Bot
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } }
};

// ═════════ INTERACTIVE DAILY OPS DEMO ═════════
const DAILY_OPS_TABS = [
  { key: 'bath',      label: 'Bath List',       icon: Bath,          color: 'blue'    },
  { key: 'play',      label: 'Private Play',    icon: PawPrint,      color: 'purple'  },
  { key: 'rooms',     label: 'Rooms',           icon: Home,          color: 'amber'   },
  { key: 'curve',     label: 'Booking Curve',   icon: TrendingUp,    color: 'emerald' },
  { key: 'churn',     label: 'Churn',           icon: TrendingDown,  color: 'rose'    },
  { key: 'occupancy', label: 'Occupancy Trend', icon: Activity,      color: 'teal'    }
];

const InteractiveDailyOpsDemo = () => {
  const [active, setActive] = React.useState('rooms');

  return (
    <div className="rounded-xl overflow-hidden border border-white/10 shadow-[0_8px_60px_rgba(0,0,0,0.5)] bg-gradient-to-br from-slate-800 to-slate-900">
      {/* Window chrome */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <div className="text-xs text-slate-500">Daily Ops Dashboard — Interactive Demo</div>
      </div>

      {/* Tab strip — clickable */}
      <div className="flex gap-2 p-3 border-b border-white/5 overflow-x-auto">
        {DAILY_OPS_TABS.map((t) => {
          const isActive = active === t.key;
          const Icon = t.icon;
          const activeClass = {
            blue:    'bg-blue-500 text-white shadow-md shadow-blue-500/30',
            purple:  'bg-purple-500 text-white shadow-md shadow-purple-500/30',
            amber:   'bg-amber-500 text-white shadow-md shadow-amber-500/30',
            emerald: 'bg-emerald-500 text-white shadow-md shadow-emerald-500/30',
            rose:    'bg-rose-500 text-white shadow-md shadow-rose-500/30',
            teal:    'bg-teal-500 text-white shadow-md shadow-teal-500/30'
          }[t.color];
          return (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              data-testid={`landing-daily-ops-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                isActive ? activeClass : 'text-slate-400 hover:bg-slate-700/30 hover:text-white'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content — animated switch */}
      <motion.div
        key={active}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="p-6 min-h-[440px]"
      >
        {active === 'bath' && <BathListPanel />}
        {active === 'play' && <PrivatePlayPanel />}
        {active === 'rooms' && <RoomsPanel />}
        {active === 'curve' && <BookingCurvePanel />}
        {active === 'churn' && <ChurnPanel />}
        {active === 'occupancy' && <OccupancyPanel />}
      </motion.div>
    </div>
  );
};

// KPI mini-card used inside panels
const MiniKpi = ({ label, val, c }) => (
  <div className={`bg-gradient-to-br ${c} rounded-lg p-3 text-white`}>
    <div className="text-xs opacity-80">{label}</div>
    <div className="text-2xl font-bold">{val}</div>
  </div>
);

// Panel 1 — Bath List
const BathListPanel = () => {
  const rows = [
    { name: 'Biscuit',    owner: 'The Thompsons',  time: '11:30 AM', type: 'Pre-Stay',   status: 'Done',        statusColor: 'bg-emerald-500/20 text-emerald-400' },
    { name: 'Moose',      owner: 'Jennifer P.',    time: '12:15 PM', type: 'Departure',  status: 'In Progress', statusColor: 'bg-amber-500/20 text-amber-400' },
    { name: 'Daisy',      owner: 'The Kims',       time: '1:00 PM',  type: 'Add-on',     status: 'Scheduled',   statusColor: 'bg-blue-500/20 text-blue-400' },
    { name: 'Rocky',      owner: 'Mark L.',        time: '2:30 PM',  type: 'Departure',  status: 'Scheduled',   statusColor: 'bg-blue-500/20 text-blue-400' },
    { name: 'Hazel',      owner: 'The Garcia\'s',  time: '3:15 PM',  type: 'Pre-Stay',   status: 'Scheduled',   statusColor: 'bg-blue-500/20 text-blue-400' }
  ];
  return (
    <>
      <div className="grid grid-cols-4 gap-3 mb-5">
        <MiniKpi label="Total Baths"  val="14" c="from-blue-500 to-indigo-600" />
        <MiniKpi label="Completed"    val="6"  c="from-emerald-500 to-green-600" />
        <MiniKpi label="In Progress"  val="2"  c="from-amber-500 to-orange-600" />
        <MiniKpi label="Revenue"      val="$840" c="from-purple-500 to-indigo-600" />
      </div>
      <div className="bg-slate-800/50 rounded-lg border border-white/5 overflow-hidden">
        <div className="grid grid-cols-[1.4fr_1.4fr_1fr_1fr_1fr] gap-2 p-3 bg-slate-700/40 text-[10px] text-slate-400 font-bold uppercase">
          <div>Dog</div><div>Owner</div><div>Time</div><div>Type</div><div>Status</div>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1.4fr_1.4fr_1fr_1fr_1fr] gap-2 p-3 text-xs border-t border-white/5 items-center">
            <div className="text-white font-medium">{r.name}</div>
            <div className="text-slate-400">{r.owner}</div>
            <div className="text-slate-300">{r.time}</div>
            <div className="text-slate-300">{r.type}</div>
            <div><span className={`px-2 py-1 rounded font-semibold ${r.statusColor}`}>{r.status}</span></div>
          </div>
        ))}
      </div>
    </>
  );
};

// Panel 2 — Private Play
const PrivatePlayPanel = () => {
  const dogs = [
    { name: 'Cooper',  type: '1-on-1',      time: '10:00 AM', dur: '30 min', note: 'High energy' },
    { name: 'Luna',    type: 'Small Group', time: '10:30 AM', dur: '45 min', note: 'Loves puzzles' },
    { name: 'Bear',    type: '1-on-1',      time: '11:15 AM', dur: '30 min', note: 'Senior pup' },
    { name: 'Nala',    type: 'Small Group', time: '1:00 PM',  dur: '45 min', note: 'Ball obsessed' },
    { name: 'Tank',    type: '1-on-1',      time: '2:00 PM',  dur: '30 min', note: 'Shy, warm-up' },
    { name: 'Poppy',   type: 'Small Group', time: '3:00 PM',  dur: '45 min', note: 'Social butterfly' }
  ];
  return (
    <>
      <div className="grid grid-cols-4 gap-3 mb-5">
        <MiniKpi label="Total Sessions"  val="8"  c="from-purple-500 to-indigo-600" />
        <MiniKpi label="1-on-1"          val="5"  c="from-blue-500 to-indigo-600" />
        <MiniKpi label="Small Group"     val="3"  c="from-pink-500 to-rose-600" />
        <MiniKpi label="Revenue"         val="$320" c="from-emerald-500 to-green-600" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {dogs.map((d, i) => (
          <div key={i} className="bg-slate-800/50 rounded-lg p-3 border border-white/5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-white">{d.name}</div>
              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${d.type === '1-on-1' ? 'bg-blue-500/20 text-blue-400' : 'bg-pink-500/20 text-pink-400'}`}>{d.type}</span>
            </div>
            <div className="text-xs text-slate-400 mb-1 flex items-center gap-1.5">
              <Clock className="h-3 w-3" />{d.time} · {d.dur}
            </div>
            <div className="text-[11px] text-slate-500 italic">{d.note}</div>
          </div>
        ))}
      </div>
    </>
  );
};

// Panel 3 — Rooms
const RoomsPanel = () => (
  <>
    <div className="grid grid-cols-4 gap-3 mb-5">
      <MiniKpi label="Occupied"       val="62 / 102" c="from-amber-500 to-orange-600" />
      <MiniKpi label="Clean"          val="28"       c="from-emerald-500 to-green-600" />
      <MiniKpi label="Dirty"          val="12"       c="from-rose-500 to-red-600" />
      <MiniKpi label="Arrivals Today" val="18"       c="from-blue-500 to-indigo-600" />
    </div>
    <div className="grid grid-cols-6 md:grid-cols-10 gap-1.5 mb-4">
      {Array(50).fill(0).map((_, i) => {
        const state = i % 7 === 0 ? 'dirty' : i % 4 === 0 ? 'empty' : 'occupied';
        const color = state === 'dirty'
          ? 'bg-rose-500/40 border-rose-500/60'
          : state === 'empty'
            ? 'bg-slate-800/50 border-white/5'
            : 'bg-amber-500/30 border-amber-500/50';
        return <div key={i} className={`aspect-square rounded border ${color}`}></div>;
      })}
    </div>
    <div className="flex gap-4 text-[11px] text-slate-400">
      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-500/40 border border-amber-500/60" /> Occupied</span>
      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-rose-500/40 border border-rose-500/60" /> Dirty / Needs Clean</span>
      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-800/50 border border-white/10" /> Available</span>
    </div>
  </>
);

// Panel 4 — Booking Curve
const BookingCurvePanel = () => {
  // 30-day forward pacing: 2026 vs 2025 reference
  const curve26 = [18,22,28,35,48,62,55,42,38,35,32,30,42,58,72,68,55,48,52,58,45,38,42,55,62,58,48,42,38,45];
  const curve25 = [15,20,25,32,42,55,50,40,35,32,30,28,38,52,65,62,50,45,48,52,42,35,38,50,55,52,45,40,35,42];
  const maxY = 80;
  const points26 = curve26.map((v, i) => `${(i / (curve26.length - 1)) * 300},${80 - (v / maxY) * 70}`).join(' ');
  const points25 = curve25.map((v, i) => `${(i / (curve25.length - 1)) * 300},${80 - (v / maxY) * 70}`).join(' ');
  return (
    <>
      <div className="grid grid-cols-4 gap-3 mb-5">
        <MiniKpi label="Active Dogs"    val="62"   c="from-emerald-500 to-green-600" />
        <MiniKpi label="vs Last Year"   val="+14%" c="from-blue-500 to-indigo-600" />
        <MiniKpi label="Next Weekend"   val="86%"  c="from-amber-500 to-orange-600" />
        <MiniKpi label="Peak Forecast"  val="May 23" c="from-purple-500 to-indigo-600" />
      </div>
      <div className="bg-slate-800/50 rounded-lg p-4 border border-white/5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs text-slate-400 font-semibold">30-Day Forward Booking Pace</div>
          <div className="flex items-center gap-3 text-[10px]">
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-emerald-400" /> 2026</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-slate-500 border-dashed" /> 2025</span>
          </div>
        </div>
        <svg viewBox="0 0 300 90" className="w-full h-32">
          <defs>
            <linearGradient id="curveGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polygon points={`0,90 ${points26} 300,90`} fill="url(#curveGradient)" />
          <polyline points={points25} fill="none" stroke="#64748b" strokeWidth="1.5" strokeDasharray="3,3" />
          <polyline points={points26} fill="none" stroke="#10b981" strokeWidth="2" />
        </svg>
        <div className="flex justify-between text-[10px] text-slate-500 mt-1">
          <span>Today</span><span>+7d</span><span>+14d</span><span>+21d</span><span>+30d</span>
        </div>
      </div>
    </>
  );
};

// Panel 5 — Churn Analysis
const ChurnPanel = () => {
  const dogs = [
    { name: 'Bentley',  owner: 'The Reynolds',  lastVisit: 'Jan 8',  lapsed: 48, prior: '3x / wk', priority: 'High'   },
    { name: 'Winston',  owner: 'Amanda K.',     lastVisit: 'Jan 22', lapsed: 34, prior: '2x / wk', priority: 'High'   },
    { name: 'Penny',    owner: 'The Chen\'s',   lastVisit: 'Feb 3',  lapsed: 22, prior: '4x / wk', priority: 'High'   },
    { name: 'Gus',      owner: 'Robert M.',     lastVisit: 'Feb 10', lapsed: 15, prior: '2x / wk', priority: 'Medium' },
    { name: 'Olive',    owner: 'The Patels',    lastVisit: 'Feb 14', lapsed: 11, prior: '1x / wk', priority: 'Low'    }
  ];
  const pColor = (p) => p === 'High' ? 'bg-rose-500/20 text-rose-400' : p === 'Medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400';
  return (
    <>
      <div className="grid grid-cols-4 gap-3 mb-5">
        <MiniKpi label="At-Risk Dogs"     val="18"    c="from-rose-500 to-red-600" />
        <MiniKpi label="Avg Days Lapsed"  val="28"    c="from-amber-500 to-orange-600" />
        <MiniKpi label="Est. Lost Revenue" val="$2.4K" c="from-purple-500 to-indigo-600" />
        <MiniKpi label="Win-Back Rate"    val="34%"   c="from-emerald-500 to-green-600" />
      </div>
      <div className="bg-slate-800/50 rounded-lg border border-white/5 overflow-hidden">
        <div className="grid grid-cols-[1.2fr_1.4fr_1fr_0.8fr_1fr_0.8fr] gap-2 p-3 bg-slate-700/40 text-[10px] text-slate-400 font-bold uppercase">
          <div>Dog</div><div>Owner</div><div>Last Visit</div><div>Days</div><div>Prior</div><div>Priority</div>
        </div>
        {dogs.map((d, i) => (
          <div key={i} className="grid grid-cols-[1.2fr_1.4fr_1fr_0.8fr_1fr_0.8fr] gap-2 p-3 text-xs border-t border-white/5 items-center">
            <div className="text-white font-medium">{d.name}</div>
            <div className="text-slate-400">{d.owner}</div>
            <div className="text-slate-300">{d.lastVisit}</div>
            <div className="text-rose-400 font-bold">{d.lapsed}d</div>
            <div className="text-slate-300">{d.prior}</div>
            <div><span className={`px-2 py-1 rounded font-semibold ${pColor(d.priority)}`}>{d.priority}</span></div>
          </div>
        ))}
      </div>
    </>
  );
};

// Panel 6 — Occupancy Trend
const OccupancyPanel = () => {
  const months = [
    { m: 'Jan', boarding: 24, daycare: 54, boarding25: 12, daycare25: 35 },
    { m: 'Feb', boarding: 28, daycare: 57, boarding25: 19, daycare25: 43 },
    { m: 'Mar', boarding: 31, daycare: 64, boarding25: 18, daycare25: 51 },
    { m: 'Apr', boarding: 38, daycare: 54, boarding25: 26, daycare25: 56 },
    { m: 'May', boarding: 42, daycare: 58, boarding25: 28, daycare25: 54 },
    { m: 'Jun', boarding: 48, daycare: 55, boarding25: 42, daycare25: 53 }
  ];
  return (
    <>
      <div className="grid grid-cols-4 gap-3 mb-5">
        <MiniKpi label="Boarding MTD"      val="37.5%" c="from-blue-500 to-indigo-600" />
        <MiniKpi label="Daycare MTD"       val="53.6%" c="from-purple-500 to-indigo-600" />
        <MiniKpi label="YoY Daycare"       val="+12pp" c="from-emerald-500 to-green-600" />
        <MiniKpi label="Peak Window"       val="Jul 3-7" c="from-amber-500 to-orange-600" />
      </div>
      <div className="bg-slate-800/50 rounded-lg p-4 border border-white/5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs text-slate-400 font-semibold">Boarding + Daycare — 2026 vs 2025</div>
          <div className="flex items-center gap-3 text-[10px]">
            <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-blue-500" /> Boarding</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-purple-500" /> Daycare</span>
          </div>
        </div>
        <div className="grid grid-cols-6 gap-3">
          {months.map((mo, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              <div className="relative w-full h-32 flex items-end gap-0.5">
                <div className="flex-1 bg-blue-500 rounded-t" style={{ height: `${mo.boarding}%` }} title={`Boarding ${mo.boarding}%`}></div>
                <div className="flex-1 bg-purple-500 rounded-t" style={{ height: `${mo.daycare}%` }} title={`Daycare ${mo.daycare}%`}></div>
              </div>
              <div className="text-[10px] text-slate-400 font-semibold">{mo.m}</div>
              <div className="text-[9px] text-emerald-400 font-bold">+{mo.daycare - mo.daycare25}pp</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════
// INTERACTIVE EXECUTIVE DASHBOARD DEMO
// ═══════════════════════════════════════════════════════════════════
const EXEC_TABS = [
  { key: 'overview',  label: 'Overview',     icon: LayoutDashboard, color: 'amber'   },
  { key: 'mix',       label: 'Revenue Mix',  icon: Grid3x3,         color: 'blue'    },
  { key: 'locations', label: 'Locations',    icon: MapPin,          color: 'emerald' },
  { key: 'leads',     label: 'Leads Funnel', icon: Users,           color: 'purple'  }
];

const InteractiveExecDashDemo = () => {
  const [active, setActive] = React.useState('overview');

  return (
    <div className="rounded-xl overflow-hidden border border-white/10 shadow-[0_8px_60px_rgba(0,0,0,0.5)] bg-gradient-to-br from-slate-800 to-slate-900">
      {/* Window chrome */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <div className="text-xs text-slate-500">Executive Dashboard — Interactive Demo</div>
      </div>

      {/* Tab strip */}
      <div className="flex gap-2 p-3 border-b border-white/5 overflow-x-auto">
        {EXEC_TABS.map((t) => {
          const isActive = active === t.key;
          const Icon = t.icon;
          const activeClass = {
            amber:   'bg-amber-500 text-white shadow-md shadow-amber-500/30',
            blue:    'bg-blue-500 text-white shadow-md shadow-blue-500/30',
            emerald: 'bg-emerald-500 text-white shadow-md shadow-emerald-500/30',
            purple:  'bg-purple-500 text-white shadow-md shadow-purple-500/30'
          }[t.color];
          return (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              data-testid={`landing-exec-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                isActive ? activeClass : 'text-slate-400 hover:bg-slate-700/30 hover:text-white'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <motion.div
        key={active}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="p-6 min-h-[440px]"
      >
        {active === 'overview'  && <ExecOverviewPanel />}
        {active === 'mix'       && <ExecRevenueMixPanel />}
        {active === 'locations' && <ExecLocationsPanel />}
        {active === 'leads'     && <ExecLeadsPanel />}
      </motion.div>
    </div>
  );
};

// Panel 1 — Overview (KPI cards + selectable trend chart)
const ExecOverviewPanel = () => {
  const [view, setView] = React.useState('revenue');

  const revenueDaily = [
    52, 48, 61, 55, 68, 72, 58, 49, 63, 71, 66, 59, 74, 81, 77,
    69, 63, 58, 72, 85, 79, 71, 68, 64, 74, 83, 88, 82, 76, 69, 78
  ];
  const revenuePY = [
    45, 42, 54, 49, 60, 63, 52, 44, 56, 63, 58, 53, 66, 71, 68,
    60, 55, 52, 63, 74, 69, 62, 59, 57, 65, 72, 76, 71, 66, 61, 67
  ];
  const counts = [
    { b: 42, d: 55 }, { b: 40, d: 52 }, { b: 48, d: 60 }, { b: 45, d: 58 }, { b: 52, d: 62 },
    { b: 55, d: 65 }, { b: 50, d: 56 }, { b: 44, d: 51 }, { b: 52, d: 60 }, { b: 58, d: 64 },
    { b: 54, d: 62 }, { b: 49, d: 58 }, { b: 60, d: 66 }, { b: 64, d: 70 }, { b: 62, d: 68 },
    { b: 57, d: 63 }, { b: 52, d: 60 }, { b: 50, d: 57 }, { b: 58, d: 65 }, { b: 68, d: 73 },
    { b: 65, d: 70 }, { b: 59, d: 66 }, { b: 56, d: 63 }, { b: 54, d: 60 }, { b: 62, d: 68 },
    { b: 68, d: 74 }, { b: 71, d: 78 }, { b: 66, d: 73 }, { b: 61, d: 68 }, { b: 55, d: 63 }, { b: 64, d: 70 }
  ];
  const monthly12m = [
    { m: 'May', cur: 92,  py: 78 },
    { m: 'Jun', cur: 108, py: 95 },
    { m: 'Jul', cur: 135, py: 118 },
    { m: 'Aug', cur: 142, py: 124 },
    { m: 'Sep', cur: 118, py: 104 },
    { m: 'Oct', cur: 126, py: 110 },
    { m: 'Nov', cur: 145, py: 128 },
    { m: 'Dec', cur: 168, py: 148 },
    { m: 'Jan', cur: 112, py: 98  },
    { m: 'Feb', cur: 119, py: 103 },
    { m: 'Mar', cur: 128, py: 112 },
    { m: 'Apr', cur: 138, py: 119 }
  ];

  return (
    <>
      <div className="grid grid-cols-4 gap-3 mb-5">
        <MiniKpi label="Today Revenue"    val="$4,820" c="from-amber-500 to-orange-600" />
        <MiniKpi label="Month-to-Date"    val="$128K"  c="from-blue-500 to-indigo-600" />
        <MiniKpi label="Year-to-Date"     val="$1.42M" c="from-emerald-500 to-green-600" />
        <MiniKpi label="Today's Dogs"     val="87"     c="from-purple-500 to-indigo-600" />
      </div>

      <div className="bg-slate-800/50 rounded-lg p-4 border border-white/5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs text-slate-400 font-semibold">
            {view === 'revenue' ? 'Daily Revenue — Last 31 Days'
              : view === 'counts' ? 'Daily Dog Counts — Last 31 Days'
              : 'Monthly Revenue — Trailing 12 Months'}
          </div>
          <div className="flex gap-1">
            {[
              { k: 'revenue', label: 'Revenue' },
              { k: 'counts',  label: 'Dog Counts' },
              { k: '12m',     label: '12M Trend' }
            ].map((opt) => (
              <button
                key={opt.k}
                onClick={() => setView(opt.k)}
                data-testid={`landing-exec-view-${opt.k}`}
                className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all ${
                  view === opt.k
                    ? 'bg-[#C5A059] text-slate-900'
                    : 'bg-slate-700/40 text-slate-400 hover:text-white'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {view === 'revenue' && (
          <svg viewBox="0 0 310 120" className="w-full h-28">
            <defs>
              <linearGradient id="rev-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
              </linearGradient>
            </defs>
            {revenueDaily.map((v, i) => {
              const x = (i / 30) * 310;
              const yPy = 120 - (revenuePY[i] / 100) * 100;
              return <circle key={`py-${i}`} cx={x} cy={yPy} r="1.2" fill="#94a3b8" opacity="0.6" />;
            })}
            <path
              d={`M0,120 ${revenueDaily.map((v, i) => `L${(i / 30) * 310},${120 - (v / 100) * 100}`).join(' ')} L310,120 Z`}
              fill="url(#rev-grad)"
            />
            <path
              d={`M${revenueDaily.map((v, i) => `${(i / 30) * 310},${120 - (v / 100) * 100}`).join(' L')}`}
              fill="none"
              stroke="#3b82f6"
              strokeWidth="2"
            />
          </svg>
        )}

        {view === 'counts' && (
          <div className="flex items-end gap-0.5 h-28">
            {counts.map((c, i) => (
              <div key={i} className="flex-1 flex flex-col justify-end gap-0" title={`Boarding ${c.b} | Daycare ${c.d}`}>
                <div className="bg-emerald-500 rounded-t-sm" style={{ height: `${c.d}%`, minHeight: '2px' }} />
                <div className="bg-blue-600" style={{ height: `${c.b}%`, minHeight: '2px' }} />
              </div>
            ))}
          </div>
        )}

        {view === '12m' && (
          <div className="flex items-end gap-2 h-28">
            {monthly12m.map((mo, i) => {
              const maxVal = 170;
              const curH = (mo.cur / maxVal) * 100;
              const pyH = (mo.py / maxVal) * 100;
              const delta = Math.round(((mo.cur - mo.py) / mo.py) * 100);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="relative w-full flex items-end gap-0.5 h-full">
                    <div className="flex-1 bg-slate-500/40 rounded-t-sm" style={{ height: `${pyH}%` }} />
                    <div className="flex-1 bg-[#C5A059] rounded-t-sm" style={{ height: `${curH}%` }} />
                  </div>
                  <div className="text-[9px] text-slate-400 font-semibold">{mo.m}</div>
                  <div className="text-[8px] text-emerald-400 font-bold">+{delta}%</div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center gap-3 text-[10px] mt-3 pt-3 border-t border-white/5">
          {view === 'counts' ? (
            <>
              <span className="flex items-center gap-1.5 text-slate-400"><span className="w-3 h-2 rounded-sm bg-blue-600" /> Boarding</span>
              <span className="flex items-center gap-1.5 text-slate-400"><span className="w-3 h-2 rounded-sm bg-emerald-500" /> Daycare</span>
            </>
          ) : view === '12m' ? (
            <>
              <span className="flex items-center gap-1.5 text-slate-400"><span className="w-3 h-2 rounded-sm bg-[#C5A059]" /> Current</span>
              <span className="flex items-center gap-1.5 text-slate-400"><span className="w-3 h-2 rounded-sm bg-slate-500/60" /> Prior Year</span>
            </>
          ) : (
            <>
              <span className="flex items-center gap-1.5 text-slate-400"><span className="w-3 h-2 rounded-sm bg-blue-500" /> Current</span>
              <span className="flex items-center gap-1.5 text-slate-400"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> Prior Year</span>
            </>
          )}
        </div>
      </div>
    </>
  );
};

// Panel 2 — Revenue Mix Matrix with drill-down
const ExecRevenueMixPanel = () => {
  const [expanded, setExpanded] = React.useState(null);

  const rows = [
    {
      cat: 'Boarding', dot: 'bg-blue-500',
      w1: '$18.2K', w2: '$22.1K', w3: '$28.5K', mtd: '$68.8K', yoy: '+14%', yoyColor: 'text-emerald-400',
      drill: [
        { sub: 'Standard Suite', mtd: '$38.4K', yoy: '+11%' },
        { sub: 'Luxury Suite',   mtd: '$21.6K', yoy: '+22%' },
        { sub: 'Holiday Premium',mtd: '$8.8K',  yoy: '+6%'  }
      ]
    },
    {
      cat: 'Daycare', dot: 'bg-purple-500',
      w1: '$9.8K', w2: '$11.2K', w3: '$12.4K', mtd: '$33.4K', yoy: '+9%', yoyColor: 'text-emerald-400',
      drill: [
        { sub: 'Full Day',  mtd: '$24.1K', yoy: '+12%' },
        { sub: 'Half Day',  mtd: '$6.8K',  yoy: '+3%'  },
        { sub: 'Day Boarding', mtd: '$2.5K', yoy: '+22%' }
      ]
    },
    {
      cat: 'Packages', dot: 'bg-amber-500',
      w1: '$4.2K', w2: '$3.8K', w3: '$5.1K', mtd: '$13.1K', yoy: '−4%', yoyColor: 'text-rose-400',
      drill: [
        { sub: '10-Day Daycare',   mtd: '$7.2K', yoy: '−8%' },
        { sub: '30-Day Daycare',   mtd: '$4.1K', yoy: '+2%' },
        { sub: 'Boarding Bundle',  mtd: '$1.8K', yoy: '−1%' }
      ]
    },
    {
      cat: 'Add-on Services', dot: 'bg-emerald-500',
      w1: '$1.8K', w2: '$2.2K', w3: '$2.9K', mtd: '$6.9K', yoy: '+18%', yoyColor: 'text-emerald-400',
      drill: [
        { sub: 'Baths',         mtd: '$3.2K', yoy: '+24%' },
        { sub: 'Private Play',  mtd: '$2.4K', yoy: '+15%' },
        { sub: 'Training',      mtd: '$1.3K', yoy: '+9%'  }
      ]
    },
    {
      cat: 'Retail & Other', dot: 'bg-sky-500',
      w1: '$0.9K', w2: '$1.1K', w3: '$1.4K', mtd: '$3.4K', yoy: '+6%', yoyColor: 'text-emerald-400',
      drill: [
        { sub: 'Food & Treats',  mtd: '$1.9K', yoy: '+4%' },
        { sub: 'Accessories',    mtd: '$0.9K', yoy: '+11%' },
        { sub: 'Gift Cards',     mtd: '$0.6K', yoy: '+8%'  }
      ]
    }
  ];

  return (
    <div className="bg-slate-800/50 rounded-lg border border-white/5 overflow-hidden">
      <div className="grid grid-cols-[1.6fr_repeat(5,1fr)] gap-1 p-2 bg-slate-700/40 text-[10px] text-slate-400 font-bold uppercase">
        <div>Category</div>
        <div className="text-center">W1</div>
        <div className="text-center">W2</div>
        <div className="text-center">W3</div>
        <div className="text-center">MTD</div>
        <div className="text-center">YoY</div>
      </div>
      {rows.map((r, i) => {
        const isOpen = expanded === r.cat;
        return (
          <div key={i}>
            <button
              onClick={() => setExpanded(isOpen ? null : r.cat)}
              data-testid={`landing-exec-mix-row-${r.cat.toLowerCase().replace(/\s+/g, '-')}`}
              className={`w-full grid grid-cols-[1.6fr_repeat(5,1fr)] gap-1 p-2.5 text-xs border-t border-white/5 items-center transition-colors cursor-pointer ${
                isOpen ? 'bg-slate-700/30' : 'hover:bg-slate-700/20'
              }`}
            >
              <div className="flex items-center gap-2 text-white font-medium">
                <ChevronRight className={`h-3 w-3 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                <span className={`w-2 h-2 rounded-full ${r.dot}`}></span>
                {r.cat}
              </div>
              <div className="text-center text-slate-300">{r.w1}</div>
              <div className="text-center text-slate-300">{r.w2}</div>
              <div className="text-center text-slate-300">{r.w3}</div>
              <div className="text-center text-white font-semibold">{r.mtd}</div>
              <div className={`text-center font-bold ${r.yoyColor}`}>{r.yoy}</div>
            </button>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.25 }}
                className="bg-slate-900/60 border-t border-white/5 overflow-hidden"
              >
                {r.drill.map((d, di) => (
                  <div key={di} className="grid grid-cols-[1.6fr_repeat(5,1fr)] gap-1 p-2 pl-10 text-[11px] text-slate-400 border-b border-white/5 last:border-b-0">
                    <div className="text-slate-300">{d.sub}</div>
                    <div></div><div></div><div></div>
                    <div className="text-center">{d.mtd}</div>
                    <div className={`text-center font-bold ${d.yoy.startsWith('−') ? 'text-rose-400' : 'text-emerald-400'}`}>{d.yoy}</div>
                  </div>
                ))}
              </motion.div>
            )}
          </div>
        );
      })}
      <div className="p-3 bg-slate-900/40 border-t border-white/5 text-[10px] text-slate-500 text-center italic">
        Tap a category to drill down into line-item performance
      </div>
    </div>
  );
};

// Panel 3 — Locations roll-up
const ExecLocationsPanel = () => {
  const locations = [
    { name: 'Chalfont, PA',    revMtd: '$128K', dogs: 87,  occ: 82, yoy: '+14%', yoyColor: 'text-emerald-400', status: 'Healthy' },
    { name: 'Garden City, NY', revMtd: '$96K',  dogs: 64,  occ: 71, yoy: '+9%',  yoyColor: 'text-emerald-400', status: 'Healthy' },
    { name: 'Syosset, NY',     revMtd: '$72K',  dogs: 48,  occ: 58, yoy: '+22%', yoyColor: 'text-emerald-400', status: 'Ramping' },
    { name: 'Cherry Hill, NJ', revMtd: '$83K',  dogs: 56,  occ: 64, yoy: '−3%',  yoyColor: 'text-rose-400',    status: 'Watch'   }
  ];
  const rollUp = {
    rev: '$379K', dogs: 255, occ: 69, yoy: '+11%'
  };

  const statusClass = {
    Healthy: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
    Ramping: 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
    Watch:   'bg-amber-500/20 text-amber-300 border border-amber-500/30'
  };

  return (
    <>
      <div className="grid grid-cols-4 gap-3 mb-5">
        <MiniKpi label="Total MTD Revenue" val={rollUp.rev}  c="from-amber-500 to-orange-600" />
        <MiniKpi label="Dogs On-Property"  val={rollUp.dogs} c="from-blue-500 to-indigo-600" />
        <MiniKpi label="Avg Occupancy"     val={`${rollUp.occ}%`} c="from-purple-500 to-indigo-600" />
        <MiniKpi label="YoY Roll-up"       val={rollUp.yoy}  c="from-emerald-500 to-green-600" />
      </div>

      <div className="bg-slate-800/50 rounded-lg border border-white/5 overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_1fr_1.3fr_1fr_1fr] gap-2 p-2 bg-slate-700/40 text-[10px] text-slate-400 font-bold uppercase">
          <div>Location</div>
          <div className="text-center">MTD Rev</div>
          <div className="text-center">Dogs</div>
          <div className="text-center">Occupancy</div>
          <div className="text-center">YoY</div>
          <div className="text-center">Status</div>
        </div>
        {locations.map((loc, i) => (
          <div key={i} className="grid grid-cols-[2fr_1fr_1fr_1.3fr_1fr_1fr] gap-2 p-3 text-xs border-t border-white/5 items-center hover:bg-slate-700/20 transition-colors">
            <div className="flex items-center gap-2 text-white font-medium">
              <MapPin className="h-3.5 w-3.5 text-slate-500" />
              {loc.name}
            </div>
            <div className="text-center text-white font-semibold">{loc.revMtd}</div>
            <div className="text-center text-slate-300">{loc.dogs}</div>
            <div className="text-center">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-700/60 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" style={{ width: `${loc.occ}%` }} />
                </div>
                <div className="text-slate-300 text-[10px] font-semibold w-8">{loc.occ}%</div>
              </div>
            </div>
            <div className={`text-center font-bold ${loc.yoyColor}`}>{loc.yoy}</div>
            <div className="flex justify-center">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusClass[loc.status]}`}>{loc.status}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

// Panel 4 — Leads funnel
const ExecLeadsPanel = () => {
  const funnel = [
    { stage: 'New Leads',          count: 312, pct: 100, color: 'from-blue-500 to-indigo-600' },
    { stage: 'Contacted',          count: 248, pct: 79,  color: 'from-indigo-500 to-purple-600' },
    { stage: 'Tour Scheduled',     count: 142, pct: 46,  color: 'from-purple-500 to-pink-600' },
    { stage: 'Tour Completed',     count: 118, pct: 38,  color: 'from-pink-500 to-rose-600' },
    { stage: 'Converted (1st Stay)', count: 87, pct: 28, color: 'from-emerald-500 to-green-600' }
  ];
  const sources = [
    { src: 'Organic Search',  count: 98, color: 'bg-blue-500' },
    { src: 'Paid Social',     count: 76, color: 'bg-purple-500' },
    { src: 'Referral',        count: 64, color: 'bg-emerald-500' },
    { src: 'Direct / Walk-in',count: 48, color: 'bg-amber-500' },
    { src: 'Google My Business', count: 26, color: 'bg-rose-500' }
  ];

  return (
    <>
      <div className="grid grid-cols-4 gap-3 mb-5">
        <MiniKpi label="Leads MTD"        val="312"   c="from-blue-500 to-indigo-600" />
        <MiniKpi label="Conversion Rate"  val="28%"   c="from-emerald-500 to-green-600" />
        <MiniKpi label="Avg First-Stay"   val="$312"  c="from-amber-500 to-orange-600" />
        <MiniKpi label="Follow-up Queue"  val="24"    c="from-rose-500 to-pink-600" />
      </div>

      <div className="grid grid-cols-5 gap-4">
        <div className="col-span-3 bg-slate-800/50 rounded-lg p-4 border border-white/5">
          <div className="text-xs text-slate-400 font-semibold mb-3">Conversion Funnel — This Month</div>
          <div className="space-y-2">
            {funnel.map((f, i) => (
              <div key={i} className="relative">
                <div className={`bg-gradient-to-r ${f.color} rounded-md h-9 flex items-center justify-between px-3 text-white text-xs font-semibold`}
                     style={{ width: `${f.pct}%`, minWidth: '40%' }}>
                  <span>{f.stage}</span>
                  <span className="text-[11px] opacity-90">{f.count}</span>
                </div>
                {i < funnel.length - 1 && (
                  <div className="text-[10px] text-slate-500 pl-3 mt-0.5">
                    {Math.round((funnel[i + 1].count / f.count) * 100)}% → next stage
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-2 bg-slate-800/50 rounded-lg p-4 border border-white/5">
          <div className="text-xs text-slate-400 font-semibold mb-3">Top Lead Sources</div>
          <div className="space-y-3">
            {sources.map((s, i) => {
              const max = Math.max(...sources.map((x) => x.count));
              return (
                <div key={i}>
                  <div className="flex items-center justify-between text-[11px] mb-1">
                    <span className="text-slate-300 font-medium">{s.src}</span>
                    <span className="text-white font-bold">{s.count}</span>
                  </div>
                  <div className="h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
                    <div className={`h-full ${s.color} rounded-full`} style={{ width: `${(s.count / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [showDemo, setShowDemo] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', phone: '', locations: '' });
  const [demoSubmitted, setDemoSubmitted] = useState(false);

  const handleDemoSubmit = (e) => {
    e.preventDefault();
    setDemoSubmitted(true);
    setTimeout(() => { setShowDemo(false); setDemoSubmitted(false); }, 3000);
  };

  return (
    <div className="min-h-screen bg-[#020617] text-white overflow-x-hidden" style={{ fontFamily: 'Manrope, sans-serif' }}>

      {/* ═══ FLOATING NAV ═══ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#020617]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5">
            <img src="/fourpawiq-icon-gold.png" alt="FourPaw IQ" className="h-10 w-10 object-contain" />
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              FourPaw<span className="text-[#C5A059]">IQ</span>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-slate-400">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#agents" className="hover:text-white transition-colors">Agents</a>
            <a href="#daily-ops" className="hover:text-white transition-colors">Daily Ops</a>
            <a href="#exec-dashboard" className="hover:text-white transition-colors">Exec Dashboard</a>
            <a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a>
            <a href="#testimonials" className="hover:text-white transition-colors">Testimonials</a>
          </div>
          <div className="flex items-center gap-3">
            <Button
              className="bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-bold text-sm px-5 shadow-[0_0_20px_rgba(197,160,89,0.2)]"
              onClick={() => navigate('/login')}
              data-testid="nav-login-btn"
            >
              Login
            </Button>
          </div>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="relative min-h-screen flex items-center pt-16">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#1e293b]/40 via-[#020617] to-[#020617]" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#C5A059]/5 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: Copy */}
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-slate-800/50 border border-slate-700 rounded-full px-4 py-1.5 mb-8">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs text-slate-300 font-medium tracking-wide uppercase">Built for Multi-Location Pet Hotels</span>
              </motion.div>

              <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight leading-[1.05] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                The Operating System for{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C5A059] to-[#D4AF37]">
                  Pet Hotels & Resorts.
                </span>
              </motion.h1>

              <motion.p variants={fadeUp} className="text-base sm:text-lg md:text-xl text-slate-400 leading-relaxed mb-10 max-w-xl">
                Automate leads, streamline daily ops, and unlock executive-grade revenue intelligence. The complete operations platform for multi-location pet hotels — from front-desk to the C-suite.
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
                <Button
                  className="bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-bold text-base px-8 py-6 shadow-[0_0_30px_rgba(197,160,89,0.3)] transition-all hover:shadow-[0_0_40px_rgba(197,160,89,0.5)]"
                  onClick={() => setShowDemo(true)}
                  data-testid="hero-demo-btn"
                >
                  Book a Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  className="border-slate-700 text-white hover:bg-slate-800 hover:border-slate-500 font-semibold text-base px-8 py-6 backdrop-blur-md bg-slate-800/30"
                  onClick={() => navigate('/login')}
                  data-testid="hero-trial-btn"
                >
                  Start Free Trial
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-6 mt-10 text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> 14-day free trial</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> No credit card required</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Setup in 10 minutes</span>
              </motion.div>
            </motion.div>

            {/* Right: Hero Image - App Screenshot */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-lg overflow-hidden border border-white/10 shadow-[0_8px_60px_rgba(0,0,0,0.5)]">
                <img
                  src="/app-screenshot-dashboard.png"
                  alt="FourPaw IQ Dashboard showing lead management and analytics"
                  className="w-full h-[500px] object-cover object-top"
                  onError={(e) => {
                    e.target.src = "https://static.prod-images.emergentagent.com/jobs/340dc3c8-371c-42ec-886c-2c71ec680326/images/05488475973941ee2aba3cb789494682379f39988b8225885a433e56553e399d.png";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent" />
                {/* Floating stat cards */}
                <div className="absolute bottom-6 left-6 right-6 flex gap-3">
                  <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-lg px-4 py-3 flex-1">
                    <div className="text-2xl font-bold text-[#C5A059]" style={{ fontFamily: 'Outfit' }}>2,400+</div>
                    <div className="text-xs text-slate-400">Leads Captured</div>
                  </div>
                  <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-lg px-4 py-3 flex-1">
                    <div className="text-2xl font-bold text-emerald-400" style={{ fontFamily: 'Outfit' }}>42%</div>
                    <div className="text-xs text-slate-400">Conversion Rate</div>
                  </div>
                  <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-lg px-4 py-3 flex-1">
                    <div className="text-2xl font-bold text-blue-400" style={{ fontFamily: 'Outfit' }}>10h</div>
                    <div className="text-xs text-slate-400">Saved / Week</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ LOGOS / SOCIAL PROOF BAR ═══ */}
      <section className="border-y border-white/5 bg-slate-900/30 py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-xs uppercase tracking-[0.2em] text-slate-500 mb-6 font-bold">Trusted by pet hotels & resorts nationwide</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-slate-600">
            {['Chalfont, PA', 'Garden City, NY'].map((loc, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-[#C5A059]/60" />
                <span className="text-slate-400">{loc}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-sm">
              <Zap className="h-4 w-4 text-emerald-400/60" />
              <span className="text-slate-400">4 more locations coming soon</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ PROBLEM / SOLUTION ═══ */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold mb-4">The Problem</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Outfit' }}>
              Running a pet resort is hard enough.<br className="hidden sm:block" />
              <span className="text-slate-500">Your software shouldn't make it harder.</span>
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { problem: 'Leads buried in Gmail', solution: 'Auto-captured & scored instantly', icon: Mail },
              { problem: 'Hours of manual data entry', solution: '100% automated lead pipeline', icon: Clock },
              { problem: 'No visibility across locations', solution: 'One dashboard, all locations', icon: MapPin },
              { problem: 'Check-out chaos for staff', solution: 'Digital Going Home board on TV', icon: Monitor }
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp} className="group relative bg-slate-900/40 border border-white/5 hover:border-[#C5A059]/30 rounded-lg p-6 transition-all duration-500">
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C5A059]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <item.icon className="h-6 w-6 text-slate-500 mb-4" />
                <p className="text-sm text-red-400/80 line-through mb-2">{item.problem}</p>
                <p className="text-base text-white font-medium">{item.solution}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ FEATURES BENTO GRID ═══ */}
      <section id="features" className="py-24 md:py-32 bg-[#0F172A]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold mb-4">Platform Features</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Everything you need.<br /><span className="text-slate-500">Nothing you don't.</span>
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-5">
            {/* Lead Management - Large */}
            <motion.div variants={fadeUp} className="md:col-span-2 group relative bg-slate-900/40 border border-white/5 hover:border-[#C5A059]/30 rounded-lg p-8 transition-all duration-500 overflow-hidden">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#C5A059]/5 rounded-full blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                    <Users className="h-5 w-5 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>Lead Management</h3>
                </div>
                <p className="text-slate-400 leading-relaxed mb-6 max-w-lg">
                  Every email inquiry is automatically captured, parsed by AI, scored, and placed into your pipeline. Your team gets morning & afternoon email reminders so no lead ever slips through.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {['AI Lead Scoring', 'Auto Follow-ups', 'POS Conversion Tracking', 'Email Reminders', 'Service Filtering', 'CSV Export'].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Gmail Sync */}
            <motion.div variants={fadeUp} className="group relative bg-slate-900/40 border border-white/5 hover:border-[#C5A059]/30 rounded-lg p-8 transition-all duration-500">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4">
                <Mail className="h-5 w-5 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>Gmail Auto-Sync</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Connect your location's Gmail and watch leads flow in automatically. Subject filters ensure only real inquiries become leads. Zero manual work.
              </p>
            </motion.div>

            {/* AI Insights */}
            <motion.div variants={fadeUp} className="group relative bg-slate-900/40 border border-white/5 hover:border-[#C5A059]/30 rounded-lg p-8 transition-all duration-500">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-4">
                <Sparkles className="h-5 w-5 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>AI Business Insights</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Ask plain-English questions like "How's boarding looking this weekend?" and get instant answers from your Gingr data. Revenue, occupancy, customer trends — all at your fingertips.
              </p>
            </motion.div>

            {/* Going Home Board - Large */}
            <motion.div variants={fadeUp} className="md:col-span-2 group relative bg-slate-900/40 border border-white/5 hover:border-[#C5A059]/30 rounded-lg p-8 transition-all duration-500 overflow-hidden">
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-emerald-500/5 rounded-full blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <Monitor className="h-5 w-5 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>Going Home Board</h3>
                </div>
                <p className="text-slate-400 leading-relaxed mb-6 max-w-lg">
                  Cast a beautiful TV display showing which dogs are checking out today — with photos, owner names, and size groupings. Staff can send real-time pickup notifications to the daycare team with one tap.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {['TV Display Mode', 'Dog Photos from Gingr', 'Size Filtering', 'Pickup Notifications', 'Owner Search', 'Departure Bath Tracking'].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Multi-Location */}
            <motion.div variants={fadeUp} className="md:col-span-3 group relative bg-gradient-to-r from-slate-900/40 to-slate-900/20 border border-white/5 hover:border-[#C5A059]/30 rounded-lg p-8 transition-all duration-500">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 border border-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-[#C5A059]" />
                  </div>
                  <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>Multi-Location Command Center</h3>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed flex-1">
                  Switch between locations in one click. Each location gets its own leads, email config, API credentials, and AI context. Built to scale from 1 to 100+ resorts.
                </p>
                <div className="flex items-center gap-4 text-sm text-slate-300 flex-shrink-0">
                  <span className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-[#C5A059]" /> Data Isolation</span>
                  <span className="flex items-center gap-1.5"><Zap className="h-4 w-4 text-[#C5A059]" /> Instant Switching</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══ AUTOMATION AGENTS ═══ */}
      <section id="agents" className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#C5A059]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16 max-w-3xl">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-[#C5A059]/10 border border-[#C5A059]/20">
              <Bot className="h-3.5 w-3.5 text-[#C5A059]" />
              <span className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold">Automation Agents</span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Staff that never clock out.<br /><span className="text-slate-500">Working inside Gingr, 24/7.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 leading-relaxed mt-6 text-lg">
              Our agents log into your Gingr and complete the same workflows your front desk does — natively. No new system to learn, no exports, no copy-paste. Gingr stays your source of truth; the busywork just disappears.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 gap-5">
            {/* Vaccine Compliance Agent */}
            <motion.div variants={fadeUp} className="group relative bg-slate-900/40 border border-white/5 hover:border-cyan-500/30 rounded-lg p-8 transition-all duration-500 overflow-hidden">
              <div className="absolute -top-16 -right-16 w-40 h-40 bg-cyan-500/5 rounded-full blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                    <Syringe className="h-5 w-5 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>Vaccine Compliance Agent</h3>
                    <p className="text-xs text-cyan-400/80 font-medium">Auto-sends Gingr vaccine reminders</p>
                  </div>
                </div>
                <p className="text-slate-400 leading-relaxed mb-6">
                  Scans Gingr's Expired Vaccinations report and fires off native Email Reminders to every customer with an upcoming reservation and missing or expired records — so no unvaccinated dog shows up unexpected.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {['Native Gingr reminders', 'Upcoming-reservation focus', 'Duplicate-safe', 'Daily auto-run'].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />{f}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Unsigned Agreements Agent */}
            <motion.div variants={fadeUp} className="group relative bg-slate-900/40 border border-white/5 hover:border-violet-500/30 rounded-lg p-8 transition-all duration-500 overflow-hidden">
              <div className="absolute -top-16 -right-16 w-40 h-40 bg-violet-500/5 rounded-full blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                    <FileSignature className="h-5 w-5 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>Unsigned Agreements Agent</h3>
                    <p className="text-xs text-violet-400/80 font-medium">Texts the agreement signature link</p>
                  </div>
                </div>
                <p className="text-slate-400 leading-relaxed mb-6">
                  Finds customers with unsigned policies for upcoming stays, pushes the agreement to their device, and texts the signature-capture link — all through Gingr's own workflow. Paperwork signed before drop-off.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {['Push-to-device + SMS', 'Only sends with a phone on file', 'Duplicate-safe', 'Daily auto-run'].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />{f}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Platform strip */}
            <motion.div variants={fadeUp} className="md:col-span-2 group relative bg-gradient-to-r from-slate-900/50 to-slate-900/20 border border-white/5 hover:border-[#C5A059]/30 rounded-lg p-8 transition-all duration-500">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex items-center gap-3 md:w-1/3">
                  <div className="w-11 h-11 rounded-lg bg-[#C5A059]/10 border border-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-5 w-5 text-[#C5A059]" />
                  </div>
                  <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>A growing agent platform</h3>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed flex-1">
                  New agents ship regularly — deposit reminders, review requests, waitlist fills and more. Every agent starts in a safe <span className="text-slate-200">dry-run</span> (counts who'd be contacted, sends nothing) until you explicitly turn it live, and each run keeps a full audit trail with screenshots.
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-300 flex-shrink-0">
                  <span className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-[#C5A059]" /> Dry-run first</span>
                  <span className="flex items-center gap-1.5"><Zap className="h-4 w-4 text-[#C5A059]" /> Native to Gingr</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>


      {/* ═══ DAY-TO-DAY OPERATIONS ═══ */}
      <section id="daily-ops" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold mb-4">Day-to-Day Operations</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Streamline your daily workflow.<br /><span className="text-slate-500">Less chaos, more care.</span>
            </motion.h2>
          </motion.div>

          {/* Going Home Board Feature */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <motion.div variants={fadeUp}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Monitor className="h-6 w-6 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit' }}>Going Home Board</h3>
              </div>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Transform your checkout process with a beautiful TV display showing all dogs departing today. Staff can instantly see who's leaving, send real-time pickup notifications, and keep parents informed.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {['Live photo displays from Gingr', 'Size-based color coding', 'One-tap pickup alerts', 'Owner contact info', 'Bath/grooming status', 'Playgroup flag indicators'].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div variants={fadeUp} className="relative">
              <div className="rounded-xl overflow-hidden border border-white/10 shadow-[0_8px_60px_rgba(0,0,0,0.5)] bg-gradient-to-br from-slate-800 to-slate-900">
                {/* Mock App UI - Going Home Board */}
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs text-slate-500">Going Home Board - Live View</div>
                </div>
                <div className="p-6">
                  {/* Mock stat cards */}
                  <div className="grid grid-cols-4 gap-3 mb-6">
                    <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg p-3 text-white">
                      <div className="text-xs opacity-80">Total Dogs</div>
                      <div className="text-2xl font-bold">24</div>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg p-3 text-white">
                      <div className="text-xs opacity-80">Small</div>
                      <div className="text-2xl font-bold">12</div>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg p-3 text-white">
                      <div className="text-xs opacity-80">Large</div>
                      <div className="text-2xl font-bold">12</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg p-3 text-white">
                      <div className="text-xs opacity-80">Owners</div>
                      <div className="text-2xl font-bold">18</div>
                    </div>
                  </div>
                  {/* Mock dog cards with actual dog images */}
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { name: 'Max', time: '5:30 PM', img: 'https://images.unsplash.com/photo-1549308423-a9b1b61cd4b1?w=200&h=200&fit=crop' },
                      { name: 'Bella', time: '4:00 PM', img: 'https://images.unsplash.com/photo-1767986919117-8c01b1cfdcf0?w=200&h=200&fit=crop' },
                      { name: 'Charlie', time: '6:00 PM', img: 'https://images.pexels.com/photos/32625782/pexels-photo-32625782.png?auto=compress&w=200&h=200&fit=crop' },
                      { name: 'Luna', time: '5:00 PM', img: 'https://images.pexels.com/photos/12681762/pexels-photo-12681762.jpeg?auto=compress&w=200&h=200&fit=crop' }
                    ].map((dog, i) => (
                      <div key={i} className="bg-slate-800/50 rounded-lg p-3 border border-white/5">
                        <div className="w-full aspect-square rounded-lg mb-2 overflow-hidden">
                          <img src={dog.img} alt={dog.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="text-sm font-medium text-white">{dog.name}</div>
                        <div className="text-xs text-slate-400">Pickup: {dog.time}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Follow-Up Queue Feature */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <motion.div variants={fadeUp} className="order-2 lg:order-1 relative">
              <div className="rounded-xl overflow-hidden border border-white/10 shadow-[0_8px_60px_rgba(0,0,0,0.5)] bg-gradient-to-br from-slate-800 to-slate-900">
                {/* Mock App UI - Follow-Up Queue */}
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs text-slate-500">Follow-Up Queue</div>
                </div>
                <div className="p-6">
                  {/* Mock priority cards */}
                  <div className="grid grid-cols-4 gap-3 mb-6">
                    <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-lg p-3 text-white">
                      <div className="text-xs opacity-80">High Priority</div>
                      <div className="text-2xl font-bold">12</div>
                    </div>
                    <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg p-3 text-white">
                      <div className="text-xs opacity-80">Medium</div>
                      <div className="text-2xl font-bold">8</div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg p-3 text-white">
                      <div className="text-xs opacity-80">Low</div>
                      <div className="text-2xl font-bold">5</div>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg p-3 text-white">
                      <div className="text-xs opacity-80">Total</div>
                      <div className="text-2xl font-bold">25</div>
                    </div>
                  </div>
                  {/* Mock lead cards */}
                  <div className="space-y-3">
                    {['Sarah Johnson', 'Mike Peters', 'Lisa Chen'].map((name, i) => (
                      <div key={i} className="bg-slate-800/50 rounded-lg p-4 border border-white/5 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                            {name[0]}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white">{name}</div>
                            <div className="text-xs text-slate-400">Dog Boarding inquiry</div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <div className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">Call</div>
                          <div className="px-3 py-1 bg-green-500/20 text-green-400 rounded text-xs">Email</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div variants={fadeUp} className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-400" />
                </div>
                <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit' }}>Smart Follow-Up Queue</h3>
              </div>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Never miss a hot lead again. Our intelligent queue prioritizes leads based on urgency, days since contact, and conversion potential. Your team knows exactly who to call next.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {['Priority-based sorting', 'One-click action logging', 'Rep performance tracking', 'Quick call/email/SMS', 'AI-powered suggestions', 'Conversion tracking'].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="h-4 w-4 text-purple-500 flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Rep Activity Tracking */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-blue-400" />
                </div>
                <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit' }}>Rep Activity & Accountability</h3>
              </div>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                See exactly what each team member accomplished. Track calls, emails, and conversions by rep. Generate reports for any date range and ensure accountability across your team.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {['Per-rep action counts', 'Date range filtering', 'Lead drill-down', 'Top performer badges', 'Exportable reports', 'Real-time updates'].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div variants={fadeUp} className="relative">
              <div className="rounded-xl overflow-hidden border border-white/10 shadow-[0_8px_60px_rgba(0,0,0,0.5)] bg-gradient-to-br from-slate-800 to-slate-900">
                {/* Mock App UI - Rep Activity */}
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs text-slate-500">Rep Activity Report</div>
                </div>
                <div className="p-6">
                  {/* Mock summary cards */}
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg p-3 text-white">
                      <div className="text-xs opacity-80">Total Reps</div>
                      <div className="text-2xl font-bold">6</div>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg p-3 text-white">
                      <div className="text-xs opacity-80">Total Actions</div>
                      <div className="text-2xl font-bold">142</div>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg p-3 text-white">
                      <div className="text-xs opacity-80">This Week</div>
                      <div className="text-2xl font-bold">38</div>
                    </div>
                  </div>
                  {/* Mock table */}
                  <div className="bg-slate-800/50 rounded-lg border border-white/5 overflow-hidden">
                    <div className="grid grid-cols-5 gap-2 p-3 bg-slate-700/30 text-xs text-slate-400 font-medium">
                      <div>Rep Name</div>
                      <div className="text-center">Calls</div>
                      <div className="text-center">Emails</div>
                      <div className="text-center">Total</div>
                      <div className="text-center">Leads</div>
                    </div>
                    {[
                      { name: 'Jules D. 🏆', calls: 24, emails: 18, total: 42, leads: 15 },
                      { name: 'Sarah M.', calls: 18, emails: 12, total: 30, leads: 11 },
                      { name: 'Mike R.', calls: 15, emails: 10, total: 25, leads: 9 }
                    ].map((rep, i) => (
                      <div key={i} className="grid grid-cols-5 gap-2 p-3 text-sm border-t border-white/5">
                        <div className="text-white font-medium">{rep.name}</div>
                        <div className="text-center"><span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded">{rep.calls}</span></div>
                        <div className="text-center"><span className="px-2 py-1 bg-green-500/20 text-green-400 rounded">{rep.emails}</span></div>
                        <div className="text-center"><span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded font-bold">{rep.total}</span></div>
                        <div className="text-center text-slate-400">{rep.leads}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* AI Insights Feature */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid lg:grid-cols-2 gap-12 items-center mt-20">
            <motion.div variants={fadeUp} className="order-2 lg:order-1 relative">
              <div className="rounded-xl overflow-hidden border border-white/10 shadow-[0_8px_60px_rgba(0,0,0,0.5)] bg-gradient-to-br from-slate-800 to-slate-900">
                {/* Mock App UI - AI Insights Chat */}
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-purple-400" />
                    AI Business Insights
                  </div>
                </div>
                <div className="p-6">
                  {/* Chat messages */}
                  <div className="space-y-4 mb-6">
                    {/* User message */}
                    <div className="flex justify-end">
                      <div className="bg-[#C5A059] text-slate-900 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%]">
                        <p className="text-sm font-medium">How are leads performing this month?</p>
                      </div>
                    </div>
                    {/* AI response */}
                    <div className="flex justify-start">
                      <div className="bg-slate-700/50 text-white rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] border border-white/5">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-purple-400" />
                          <span className="text-xs text-purple-400 font-medium">AI Insights</span>
                        </div>
                        <p className="text-sm leading-relaxed">Great news! This month you've captured <span className="text-emerald-400 font-semibold">142 leads</span> with a <span className="text-emerald-400 font-semibold">38% conversion rate</span>. That's up 12% from last month!</p>
                        <div className="mt-3 grid grid-cols-3 gap-2">
                          <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                            <div className="text-lg font-bold text-emerald-400">142</div>
                            <div className="text-xs text-slate-400">New Leads</div>
                          </div>
                          <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                            <div className="text-lg font-bold text-blue-400">54</div>
                            <div className="text-xs text-slate-400">Converted</div>
                          </div>
                          <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                            <div className="text-lg font-bold text-purple-400">$18.2K</div>
                            <div className="text-xs text-slate-400">Revenue</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Another user message */}
                    <div className="flex justify-end">
                      <div className="bg-[#C5A059] text-slate-900 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%]">
                        <p className="text-sm font-medium">Which service is most popular?</p>
                      </div>
                    </div>
                    {/* AI response */}
                    <div className="flex justify-start">
                      <div className="bg-slate-700/50 text-white rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] border border-white/5">
                        <p className="text-sm leading-relaxed"><span className="text-amber-400 font-semibold">Dog Boarding</span> leads with 48% of inquiries, followed by Daycare at 32%. Consider promoting your boarding packages!</p>
                      </div>
                    </div>
                  </div>
                  {/* Input area */}
                  <div className="flex gap-2">
                    <div className="flex-1 bg-slate-700/30 border border-white/10 rounded-lg px-4 py-3 text-sm text-slate-400">
                      Ask anything about your business...
                    </div>
                    <button className="px-4 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg">
                      <ArrowRight className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div variants={fadeUp} className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-purple-400" />
                </div>
                <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit' }}>AI Business Insights</h3>
              </div>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Ask questions in plain English and get instant answers about your business. Revenue trends, lead performance, conversion rates, seasonal patterns — all at your fingertips without digging through reports.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {['Natural language queries', 'Revenue & conversion insights', 'Lead source analysis', 'Seasonal trend detection', 'Performance comparisons', 'Actionable recommendations'].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="h-4 w-4 text-purple-500 flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══ DAILY OPS COMMAND CENTER ═══ */}
      <section className="py-24 md:py-32 bg-[#0F172A]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold mb-4">Daily Ops Command Center</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Every dog. Every room.<br /><span className="text-slate-500">One operational view.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg mt-6 max-w-2xl leading-relaxed">
              Six purpose-built tabs replace the spreadsheets, sticky notes, and shift-hand-off confusion. Your team opens one page and knows exactly what today looks like.
            </motion.p>
          </motion.div>

          {/* Tabbed Mock UI — fully interactive */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-16">
            <InteractiveDailyOpsDemo />
          </motion.div>

          {/* Six tab feature cards */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'Bath List', desc: 'Arriving dogs scheduled for a bath plus end-of-stay grooming. Check off as completed in real time.', icon: Bath, color: 'blue' },
              { title: 'Private Play', desc: 'Dogs booked for 1-on-1 or small-group play. Know who needs enrichment before pickup.', icon: PawPrint, color: 'purple' },
              { title: 'Rooms', desc: 'Exact room assignments, clean/dirty status, and check-in/out timing. No more guesswork at hand-off.', icon: Home, color: 'amber' },
              { title: 'Booking Curve', desc: 'Forward-looking reservation pacing vs prior year. Catch weak weekends early, double down on strong ones.', icon: TrendingUp, color: 'emerald' },
              { title: 'Churn Analysis', desc: 'Identify regular daycare dogs who stopped coming. Automatic win-back list for your team.', icon: TrendingDown, color: 'rose' },
              { title: 'Occupancy Trend', desc: 'Monthly boarding + daycare trends with weekly snapshots, holiday peak windows, and YoY comparison.', icon: Activity, color: 'teal' }
            ].map((t, i) => (
              <motion.div key={i} variants={fadeUp} className="group relative bg-slate-900/40 border border-white/5 hover:border-[#C5A059]/30 rounded-lg p-6 transition-all duration-500">
                <div className={`w-10 h-10 rounded-lg bg-${t.color}-500/10 border border-${t.color}-500/20 flex items-center justify-center mb-4`}>
                  <t.icon className={`h-5 w-5 text-${t.color}-400`} />
                </div>
                <h3 className="text-base font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>{t.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{t.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ EXECUTIVE DASHBOARD ═══ */}
      <section id="exec-dashboard" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold mb-4">Executive Dashboard</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Your business,<br /><span className="text-slate-500">at a glance.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg mt-6 max-w-2xl leading-relaxed">
              Tableau-grade revenue intelligence without the Tableau invoice. Owners and GMs open one screen and know the health of the business — today, this month, and the last 12 months.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid lg:grid-cols-5 gap-10 items-start mb-12">
            <motion.div variants={fadeUp} className="lg:col-span-2 lg:sticky lg:top-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#C5A059]/10 border border-[#C5A059]/20 flex items-center justify-center">
                  <LayoutDashboard className="h-6 w-6 text-[#C5A059]" />
                </div>
                <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit' }}>One screen. Whole business.</h3>
              </div>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                KPI cards, trailing 12-month trends, revenue mix with drill-downs, multi-location roll-ups, and a live leads funnel — all in one Tableau-grade view. Refreshed live from your POS.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-8">
                {[
                  'Live POS revenue sync',
                  'Current-day / MTD / YTD',
                  "Today's active dogs count",
                  '12-month trailing trend',
                  'Revenue Mix drill-downs',
                  'Multi-location roll-up',
                  'Leads funnel + sources',
                  'Calendar-aligned YoY math'
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="h-4 w-4 text-[#C5A059] flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 italic">Tap the tabs on the right to explore each view →</p>
            </motion.div>

            <motion.div variants={fadeUp} className="lg:col-span-3 relative">
              <InteractiveExecDashDemo />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══ LODGING MANAGEMENT ═══ */}
      <section className="py-24 md:py-32 bg-[#0F172A]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold mb-4">Lodging & Boarding Management</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Built for pet hotels.<br /><span className="text-slate-500">Not generic businesses.</span>
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { 
                title: 'Boarding Visibility', 
                desc: 'See exactly who\'s checking in and out. Filter by daycare, boarding, or private play. Know your capacity at a glance.',
                icon: Building2,
                color: 'amber'
              },
              { 
                title: 'Service-Based Lead Routing', 
                desc: 'Leads are automatically tagged by service interest — boarding, daycare, grooming, or training. Route to the right team.',
                icon: PawPrint,
                color: 'emerald'
              },
              { 
                title: 'AI Insights for Lodging', 
                desc: 'Ask "How\'s boarding looking this weekend?" and get instant answers. Revenue forecasts, occupancy trends, seasonal patterns.',
                icon: Sparkles,
                color: 'purple'
              },
              { 
                title: 'Gingr POS Integration', 
                desc: 'Connects directly to your Gingr system. Pull customer data, track conversions, and sync lead info automatically.',
                icon: Zap,
                color: 'blue'
              },
              { 
                title: 'Multi-Location Support', 
                desc: 'Manage 1 location or 100. Each resort gets isolated data, separate API configs, and location-specific settings.',
                icon: MapPin,
                color: 'rose'
              },
              { 
                title: 'Playgroup & Size Tracking', 
                desc: 'Visual indicators for dog size and playgroup assignments. Staff can see at a glance which dogs go where.',
                icon: Users,
                color: 'cyan'
              }
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp} className="group relative bg-slate-900/40 border border-white/5 hover:border-[#C5A059]/30 rounded-lg p-8 transition-all duration-500">
                <div className={`w-12 h-12 rounded-xl bg-${item.color}-500/10 border border-${item.color}-500/20 flex items-center justify-center mb-6`}>
                  <item.icon className={`h-6 w-6 text-${item.color}-400`} />
                </div>
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how-it-works" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold mb-4">How It Works</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Up and running in <span className="text-[#C5A059]">10 minutes</span>.
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Connect Your Systems', desc: 'Link your Gmail and Gingr POS. We handle the rest — no IT team needed.', icon: Zap },
              { step: '02', title: 'Leads Flow In Automatically', desc: 'Every booking inquiry is captured, parsed by AI, and added to your pipeline with zero manual entry.', icon: Mail },
              { step: '03', title: 'Make Data-Driven Decisions', desc: 'Ask the AI anything about your business. Track conversions. Scale with confidence.', icon: BarChart3 }
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp} className="relative">
                <div className="text-6xl font-bold text-slate-800/50 mb-4" style={{ fontFamily: 'Outfit' }}>{item.step}</div>
                <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 border border-[#C5A059]/20 flex items-center justify-center mb-4">
                  <item.icon className="h-5 w-5 text-[#C5A059]" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section id="testimonials" className="py-24 md:py-32 bg-[#0F172A]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold mb-4">Testimonials</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Loved by pet hotel teams.
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: "We went from manually entering leads in spreadsheets to having everything automated. FourPaw IQ saves my front desk team 10+ hours every week. The AI insights alone are worth it — I just ask 'how's boarding this weekend?' and get instant answers.",
                name: 'Sarah J.',
                role: 'Manager',
                location: 'Chalfont, PA',
                stars: 5
              },
              {
                quote: "Between the Going Home board, Follow-Up Queue, and Daily Ops tabs, everything I used to juggle across three spreadsheets now lives in one place. Bath lists, private play, room assignments, booking curves — it's all there. I easily save 2 hours a day on admin work.",
                name: 'Jules D.',
                role: 'Manager',
                location: 'Chalfont, PA',
                stars: 5
              },
              {
                quote: "Managing leads across multiple locations used to be a nightmare. Now I switch locations in one click and see everything — which leads need follow-up, what's converting, and where to focus. The morning email reminders keep my team accountable.",
                name: 'Glenn V.',
                role: 'Multi-Location Operations',
                location: 'Garden City, NY',
                stars: 5
              }
            ].map((t, i) => (
              <motion.div key={i} variants={fadeUp} className="bg-slate-900/40 border border-white/5 rounded-lg p-8">
                <div className="flex gap-1 mb-4">
                  {Array(t.stars).fill(0).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-[#C5A059] text-[#C5A059]" />
                  ))}
                </div>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">"{t.quote}"</p>
                <div>
                  <div className="text-white font-semibold text-sm">{t.name}</div>
                  <div className="text-slate-500 text-xs">{t.role}</div>
                  <div className="text-slate-600 text-xs flex items-center gap-1 mt-1">
                    <MapPin className="h-3 w-3" /> {t.location}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-24 md:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#0F172A] to-[#020617]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#C5A059]/5 rounded-full blur-[100px]" />

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="relative max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit' }}>
            Ready to transform how you<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C5A059] to-[#D4AF37]">run your pet hotel?</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-slate-400 text-lg mb-10 max-w-xl mx-auto">
            Join pet hotel teams who are saving 10+ hours per week, converting more leads, and making smarter decisions with AI.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              className="bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-bold text-lg px-10 py-6 shadow-[0_0_30px_rgba(197,160,89,0.3)] transition-all hover:shadow-[0_0_40px_rgba(197,160,89,0.5)]"
              onClick={() => setShowDemo(true)}
              data-testid="final-demo-btn"
            >
              Book a Demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              className="border-slate-700 text-white hover:bg-slate-800 hover:border-slate-500 font-semibold text-lg px-10 py-6 backdrop-blur-md bg-slate-800/30"
              onClick={() => navigate('/login')}
              data-testid="final-trial-btn"
            >
              Start Free Trial
            </Button>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="border-t border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2.5">
              <img src="/fourpawiq-icon-gold.png" alt="FourPaw IQ" className="h-9 w-9 object-contain" />
              <span className="text-sm font-bold" style={{ fontFamily: 'Outfit' }}>
                FourPaw<span className="text-[#C5A059]">IQ</span>
              </span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <a href="#features" className="hover:text-slate-300 transition-colors">Features</a>
              <a href="#operations" className="hover:text-slate-300 transition-colors">Operations</a>
              <a href="#testimonials" className="hover:text-slate-300 transition-colors">Testimonials</a>
            </div>
            <p className="text-xs text-slate-600">
              &copy; {new Date().getFullYear()} Ariya Ventures Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* ═══ DEMO MODAL ═══ */}
      {showDemo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowDemo(false)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative bg-[#0F172A] border border-white/10 rounded-lg p-8 w-full max-w-md shadow-[0_8px_60px_rgba(0,0,0,0.5)]"
          >
            <button onClick={() => setShowDemo(false)} className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors">
              <X className="h-5 w-5" />
            </button>

            {demoSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Demo Requested!</h3>
                <p className="text-slate-400 text-sm">We'll reach out within 24 hours to schedule your personalized demo.</p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>Book a Demo</h3>
                  <p className="text-slate-400 text-sm">See FourPaw IQ in action with a personalized walkthrough.</p>
                </div>
                <form onSubmit={handleDemoSubmit} className="space-y-4">
                  <Input
                    placeholder="Full Name"
                    value={demoForm.name}
                    onChange={e => setDemoForm({ ...demoForm, name: e.target.value })}
                    required
                    className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]/50"
                    data-testid="demo-name-input"
                  />
                  <Input
                    type="email"
                    placeholder="Work Email"
                    value={demoForm.email}
                    onChange={e => setDemoForm({ ...demoForm, email: e.target.value })}
                    required
                    className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]/50"
                    data-testid="demo-email-input"
                  />
                  <Input
                    type="tel"
                    placeholder="Phone Number"
                    value={demoForm.phone}
                    onChange={e => setDemoForm({ ...demoForm, phone: e.target.value })}
                    className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]/50"
                    data-testid="demo-phone-input"
                  />
                  <Input
                    placeholder="Number of Locations"
                    value={demoForm.locations}
                    onChange={e => setDemoForm({ ...demoForm, locations: e.target.value })}
                    className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]/50"
                    data-testid="demo-locations-input"
                  />
                  <Button
                    type="submit"
                    className="w-full bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-bold py-5 shadow-[0_0_20px_rgba(197,160,89,0.3)]"
                    data-testid="demo-submit-btn"
                  >
                    Request Demo
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
;
